import { useEffect, useState } from "react";
import classNames from "classnames";
import { Container, Row, Col } from "react-grid-system";
import { ToolType } from "../AccountDetails";
import { toolTypeToName } from "../AccountDetails/utils";
import toolStyles from "../../sharedStyles/Tool.module.css";
import Collapsible from "../common/Collapsible";
import StyledButton from "../common/buttons/StyledButton";
import styles from "./TextBankingServices.module.css";
import ConversationScript from "../common/ConversationScript";
import InfoValueTable, { InfoValue } from "../common/InfoValueTable";
import { TextServiceStatus } from "../../lib/api/models";
import {
  useGetTextBankingServices,
  useStopFraudAlertService,
  useStopFteuService,
} from "../../lib/api/queries";
import {
  textBanking03,
  title03,
  textBanking02,
  textBanking30051,
  title02,
  title30051,
  textBanking30052,
  title30052,
} from "../../lib/scripts/textBankingServices/textBankingServicesScripts";
import {
  formatInCentralTime,
  INPUT_MASKS,
  maskInput,
} from "../../lib/formatting";
import TextButton from "../common/buttons/TextButton";
import StopServiceConfirmationModal from "./StopServiceConfirmationModal";
import Spinner from "../common/Spinner";
import Tooltip from "../common/Tooltip";

const strings = {
  fteuTitle: "Free to End User Services",
  fraudAlertTitle: "Fraud Text Alerts",
  fteuPhoneNumber: "Text Banking Phone #:",
  fraudPhoneNumber: "Text Alerts Phone #:",
  carrier: "Carrier:",
  subscriptionStatus: "Subscription Status:",
  status: "Status:",
  dateTime: "Date/Time:",
  close: "Close",
  script: "Script",
  stopPhone: "Stop Phone",
  notAdded: "Not Added",
  fteuTooltipText1:
    "FTEU [Free To End User], is a free text message alert service that the Bank provides to the customer's cell phone. The customer's cell phone carrier and the Bank do not charge for this service.",
  fteuTooltipText2:
    "Text messages include the Welcome message, Missed Payment Due Date message, and Pre-Charged Off message.",
  fteuTooltipText3:
    "The customer is only enrolled when they provide their cell phone number to the Bank and the cell phone carrier has certified this service.",
  stopFteuTitle: "Confirm Stop Free to End User Service",
};

type Props = {
  dwbuid: string;
  className?: string;
  toolId: string;
  referenceId?: string;
  onDisplayGeneralErrors: (err: unknown) => void;
  onRequestClose: () => void;
};

const renderBadge = (status: TextServiceStatus) => (
  <div
    className={classNames(
      styles.statusBadge,
      status === "ADDED" ? styles.primary : styles.secondary,
    )}
  >
    {status[0] + status.slice(1).toLowerCase()}
  </div>
);

const mapTextServiceStatus = (status: TextServiceStatus): string => {
  switch (status) {
    case "NOT_ADDED":
      return "Not Added";
    default:
      return status[0] + status.slice(1).toLowerCase();
  }
};

const renderPhoneValue = (
  phoneNumber: string,
  status: TextServiceStatus,
  onClick: () => void,
) => (
  <div className={styles.rowItem}>
    {maskInput(INPUT_MASKS.phoneNumber, phoneNumber)}
    {status === "ADDED" ? (
      <TextButton onClick={onClick}>{strings.stopPhone}</TextButton>
    ) : null}
  </div>
);

const fteuTooltipContent = () => (
  <div className={styles.tooltipText}>
    <p>{strings.fteuTooltipText1}</p>
    <p>{strings.fteuTooltipText2}</p>
    <p>{strings.fteuTooltipText3}</p>
  </div>
);

export default function TextBankingServices({
  dwbuid,
  className,
  toolId,
  referenceId,
  onDisplayGeneralErrors,
  onRequestClose,
}: Props) {
  const { data: textBankingServices, isLoading: isLoadingTextBankingServices } =
    useGetTextBankingServices(dwbuid);
  const { mutateAsync: stopFteu, isLoading: isStopFteuLoading } =
    useStopFteuService();

  const { mutateAsync: stopFraudAlertService, isLoading: isStopFraudLoading } =
    useStopFraudAlertService();

  const fteuNotAdded =
    textBankingServices?.freeToEndUserServiceList.length === 0;
  const fraudAlertsNotAdded =
    textBankingServices?.fraudAlertService.status === "NOT_ADDED";

  const [fraudAlerts, setFraudAlerts] = useState(
    textBankingServices?.fraudAlertService.fraudAlerts ?? [],
  );
  const [fteuList, setFteuList] = useState(
    textBankingServices?.freeToEndUserServiceList ?? [],
  );
  const [showFteuStopped, setShowFteuStopped] = useState(false);
  const [showFraudStopped, setShowFraudStopped] = useState(false);
  const [selectedPhoneId, setSelectedPhoneId] = useState<string | null>(null);
  const [selectedFraudPhoneNumber, setSelectedFraudPhoneNumber] = useState<
    string | null
  >(null);
  const [isFteuExpanded, setIsFteuExpanded] = useState(true);
  const [isFraudAlertsExpanded, setIsFraudAlertsExpanded] = useState(true);

  useEffect(() => {
    setFraudAlerts(textBankingServices?.fraudAlertService.fraudAlerts ?? []);
    setFteuList(textBankingServices?.freeToEndUserServiceList ?? []);
  }, [textBankingServices]);

  return (
    <div className={classNames(toolStyles.container, className)} id={toolId}>
      <Container>
        <Row
          className={classNames(
            toolStyles.header,
            toolStyles.headerWithDivider,
          )}
        >
          <Col className={toolStyles.centerCol}>
            <h2 className={toolStyles.title}>
              {toolTypeToName(ToolType.TextBanking)}
            </h2>
          </Col>
          <Col className={toolStyles.centerCol} md={1.5}>
            <StyledButton
              className={toolStyles.headerButton}
              variant="secondary"
              onClick={onRequestClose}
            >
              {strings.close}
            </StyledButton>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            {isLoadingTextBankingServices ? (
              <Spinner />
            ) : (
              <>
                <Collapsible
                  headerText={strings.fteuTitle}
                  headerSubtext={fteuNotAdded ? strings.notAdded : undefined}
                  isExpanded={isFteuExpanded && !fteuNotAdded}
                  tooltip={<Tooltip>{fteuTooltipContent()}</Tooltip>}
                  disabled={fteuNotAdded}
                  onClick={() => {
                    setIsFteuExpanded(!isFteuExpanded);
                  }}
                >
                  {fteuList.map(
                    ({ phoneId, phoneNumber, status, carrier, updateDate }) => {
                      const tableValues = [
                        {
                          label: strings.fteuPhoneNumber,
                          value: renderPhoneValue(phoneNumber, status, () => {
                            setSelectedPhoneId(phoneId);
                          }),
                        },
                        {
                          label: strings.carrier,
                          value: carrier,
                        },
                        {
                          label: strings.subscriptionStatus,
                          value: renderBadge(status),
                        },
                      ];
                      if (updateDate) {
                        tableValues.push({
                          label: strings.dateTime,
                          value: formatInCentralTime(
                            updateDate,
                            "MM/dd/yyyy hh:mm aa z",
                          ),
                        });
                      }
                      return (
                        <div className={styles.phoneRow} key={phoneId}>
                          <InfoValueTable data={tableValues} />
                        </div>
                      );
                    },
                  )}
                </Collapsible>
                <Collapsible
                  headerText={strings.fraudAlertTitle}
                  headerSubtext={mapTextServiceStatus(
                    textBankingServices?.fraudAlertService.status ??
                      "NOT_ADDED",
                  )}
                  isExpanded={isFraudAlertsExpanded && !fraudAlertsNotAdded}
                  disabled={fraudAlertsNotAdded}
                  onClick={() => {
                    setIsFraudAlertsExpanded(!isFraudAlertsExpanded);
                  }}
                >
                  {fraudAlerts.map(
                    ({ phoneNumber, status, stoppedDate }, i) => {
                      const tableValues: InfoValue[] = [
                        {
                          label: strings.fraudPhoneNumber,
                          value: renderPhoneValue(phoneNumber, status, () => {
                            setSelectedFraudPhoneNumber(phoneNumber);
                          }),
                        },
                        {
                          label: strings.status,
                          value: renderBadge(status),
                        },
                      ];

                      if (stoppedDate) {
                        tableValues.push({
                          label: strings.dateTime,
                          value: formatInCentralTime(
                            stoppedDate,
                            "MM/dd/yyyy hh:mm aa z",
                          ),
                        });
                      }

                      return (
                        <div
                          className={styles.phoneRow}
                          key={`${phoneNumber}-${status}-${i}`}
                        >
                          <InfoValueTable data={tableValues} />
                        </div>
                      );
                    },
                  )}
                </Collapsible>
              </>
            )}
          </Col>
          <Col md={6}>
            <div className={styles.scriptColumn}>
              {showFteuStopped || showFraudStopped ? (
                <>
                  <div className={styles.scriptHeader}>
                    <div className={styles.scriptHeaderText}>
                      {strings.script}
                    </div>
                  </div>
                  <ConversationScript
                    scripts={
                      showFraudStopped ? textBanking30052() : textBanking03()
                    }
                    scriptName={showFraudStopped ? title30052 : title03}
                  />
                </>
              ) : null}
            </div>
          </Col>
        </Row>
      </Container>
      <StopServiceConfirmationModal
        title={strings.fteuTitle}
        scriptName={title02}
        scripts={textBanking02()}
        isOpen={!!selectedPhoneId}
        onSuccess={() => {
          if (!selectedPhoneId || isStopFteuLoading) {
            return;
          }
          stopFteu({ dwbuid, phoneId: selectedPhoneId, referenceId })
            .then(() => {
              setShowFteuStopped(true);
              setFteuList((prevList) => {
                const newFteu = [...prevList];
                const index = newFteu.findIndex(
                  (account) => account.phoneId === selectedPhoneId,
                );
                newFteu[index] = {
                  ...newFteu[index],
                  status: "STOPPED",
                  updateDate: new Date(),
                };
                return [...newFteu];
              });
            })
            .catch((err) => {
              onDisplayGeneralErrors(err);
            })
            .finally(() => {
              setSelectedPhoneId(null);
            });
        }}
        onRequestClose={() => {
          setSelectedPhoneId(null);
        }}
      />
      <StopServiceConfirmationModal
        title={strings.fraudAlertTitle}
        scriptName={title30051}
        scripts={textBanking30051()}
        isOpen={!!selectedFraudPhoneNumber}
        onSuccess={() => {
          if (!selectedFraudPhoneNumber || isStopFraudLoading) {
            return;
          }
          stopFraudAlertService({
            dwbuid,
            phoneNumber: selectedFraudPhoneNumber,
            referenceId,
          })
            .then(() => {
              setShowFraudStopped(true);
              // https://github.com/1fbusa/clarity-api/issues/1660
              // don't invalidate query but set element as stopped
              // until fiserv persists
              setFraudAlerts((prevAlerts) => {
                const newAlerts = [...prevAlerts];
                const index = newAlerts.findIndex(
                  (account) => account.phoneNumber === selectedFraudPhoneNumber,
                );
                newAlerts[index] = {
                  ...newAlerts[index],
                  status: "STOPPED",
                  stoppedDate: new Date(),
                };
                return [...newAlerts];
              });
            })
            .catch((err) => {
              onDisplayGeneralErrors(err);
            })
            .finally(() => {
              setSelectedFraudPhoneNumber(null);
            });
        }}
        onRequestClose={() => {
          setSelectedFraudPhoneNumber(null);
        }}
      />
    </div>
  );
}
